/** Default accent when widget settings are unavailable (matches dashboard teal). */
export const DEFAULT_EMAIL_PRIMARY_COLOR = "#0d9488";

/**
 * Responsive, email-client-safe wrapper. Uses {{primary_color}} and {{workspace_name}}
 * so the backend can inject workspace branding at send time.
 * @param {string} bodyHtml — inner fragment (headings, paragraphs, links)
 */
export function buildThemedEmailHtml(bodyHtml) {
  const inner = (bodyHtml || "").trim();
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<meta http-equiv="X-UA-Compatible" content="IE=edge"/>
<title>{{workspace_name}}</title>
</head>
<body style="margin:0;padding:0;background-color:#f1f5f9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;-webkit-font-smoothing:antialiased;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color:#f1f5f9;">
<tr><td align="center" style="padding:24px 12px;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;">
<tr><td style="background-color:{{primary_color}};padding:18px 24px;">
<span style="display:block;color:#ffffff;font-size:17px;font-weight:600;line-height:1.3;">{{workspace_name}}</span>
</td></tr>
<tr><td style="padding:24px;color:#334155;font-size:15px;line-height:1.65;">
${inner}
</td></tr>
<tr><td style="padding:16px 24px 22px;border-top:1px solid #e2e8f0;background-color:#f8fafc;color:#64748b;font-size:12px;line-height:1.5;">
Sent by {{workspace_name}}
</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

/** Link style token for inline use inside body fragments. */
export const EMAIL_LINK_STYLE =
  "color:{{primary_color}};text-decoration:underline;font-weight:500;";

/**
 * @param {string | null | undefined} text
 * @param {Record<string, string | number>} variables
 */
export function substituteEmailTemplateVars(text, variables) {
  if (!text) return "";
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
    const val = variables[key];
    return val !== undefined && val !== null ? String(val) : `{{${key}}}`;
  });
}

/**
 * Sample values for admin preview (not sent to customers).
 * @param {{ primaryColor?: string, workspaceName?: string, fromName?: string }} [opts]
 */
export function buildEmailPreviewVariables(opts = {}) {
  const primary = opts.primaryColor || DEFAULT_EMAIL_PRIMARY_COLOR;
  const workspace = opts.workspaceName || "Your workspace";
  return {
    first_name: "Alex",
    agent_name: "Jordan",
    workspace_name: workspace,
    from_name: opts.fromName || "Support Team",
    ticket_id: "TKT-1042",
    ticket_status: "open",
    updated_by: "Support Agent",
    owner_name: "Jordan Lee",
    sla_due_at: "Today, 5:00 PM",
    open_count: "12",
    pending_count: "4",
    resolved_count: "28",
    invite_link: "https://example.com/invite/preview",
    reset_link: "https://example.com/reset/preview",
    primary_color: primary,
  };
}

const inner = {
  welcome: `<p style="margin:0 0 12px;">Hi {{first_name}},</p>
<p style="margin:0 0 12px;">Welcome to {{workspace_name}}. We are excited to have you with us.</p>
<p style="margin:0;">&mdash; {{from_name}}</p>`,
  agentInvite: `<p style="margin:0 0 12px;">Hi {{agent_name}},</p>
<p style="margin:0 0 16px;">You have been invited to join {{workspace_name}}. Use the link below to activate your access:</p>
<p style="margin:0;"><a href="{{invite_link}}" style="${EMAIL_LINK_STYLE}">{{invite_link}}</a></p>`,
  conversationDigest: `<h2 style="margin:0 0 12px;font-size:18px;font-weight:600;color:#0f172a;">Conversation digest</h2>
<p style="margin:0 0 16px;">Here is your latest digest for {{workspace_name}}.</p>
<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="width:100%;max-width:360px;">
<tr>
<td style="padding:10px 12px;background-color:#f8fafc;border-radius:8px;font-size:14px;color:#334155;"><strong style="color:#0f172a;">Open</strong><br/>{{open_count}}</td>
<td style="width:8px;"></td>
<td style="padding:10px 12px;background-color:#f8fafc;border-radius:8px;font-size:14px;color:#334155;"><strong style="color:#0f172a;">Pending</strong><br/>{{pending_count}}</td>
<td style="width:8px;"></td>
<td style="padding:10px 12px;background-color:#f8fafc;border-radius:8px;font-size:14px;color:#334155;"><strong style="color:#0f172a;">Resolved</strong><br/>{{resolved_count}}</td>
</tr>
</table>`,
  slaAlerts: `<h2 style="margin:0 0 12px;font-size:18px;font-weight:600;color:#0f172a;">SLA alert</h2>
<p style="margin:0 0 12px;">Ticket <strong>{{ticket_id}}</strong> has crossed an SLA threshold.</p>
<p style="margin:0;">Assigned to: {{owner_name}}<br/>Due at: {{sla_due_at}}</p>`,
  ticketCreated: `<h2 style="margin:0 0 12px;font-size:18px;font-weight:600;color:#0f172a;">Ticket {{ticket_id}} updated</h2>
<p style="margin:0 0 8px;">Current status: <strong>{{ticket_status}}</strong></p>
<p style="margin:0;">Updated by: {{updated_by}}</p>`,
  passwordReset: `<h2 style="margin:0 0 12px;font-size:18px;font-weight:600;color:#0f172a;">Reset your password</h2>
<p style="margin:0 0 16px;">We received a request to reset your {{workspace_name}} password.</p>
<p style="margin:0 0 16px;"><a href="{{reset_link}}" style="${EMAIL_LINK_STYLE}">Continue to reset password</a></p>
<p style="margin:0;font-size:13px;color:#64748b;">If you did not request this, you can ignore this email.</p>`,
};

/** @type {Record<string, { subject: string, body: string, html: string }>} */
export const themedDefaultTemplatesByKey = {
  welcomeEmail: {
    subject: "Welcome to {{workspace_name}}",
    body: "Hi {{first_name}},\n\nWelcome to {{workspace_name}}. We are excited to have you with us.\n\n- {{from_name}}",
    html: buildThemedEmailHtml(inner.welcome),
  },
  agentInvite: {
    subject: "You are invited to join {{workspace_name}}",
    body: "Hi {{agent_name}},\n\nYou have been invited to join {{workspace_name}}. Use this link to activate your access:\n{{invite_link}}",
    html: buildThemedEmailHtml(inner.agentInvite),
  },
  conversationDigest: {
    subject: "{{workspace_name}} conversation digest",
    body: "Hello,\n\nHere is your latest digest for {{workspace_name}}.\nOpen: {{open_count}} | Pending: {{pending_count}} | Resolved: {{resolved_count}}",
    html: buildThemedEmailHtml(inner.conversationDigest),
  },
  slaAlerts: {
    subject: "SLA alert: {{ticket_id}} is at risk",
    body: "Heads up,\n\nTicket {{ticket_id}} has crossed an SLA threshold.\nAssigned to: {{owner_name}}\nDue at: {{sla_due_at}}",
    html: buildThemedEmailHtml(inner.slaAlerts),
  },
  ticketCreated: {
    subject: "Ticket {{ticket_id}} status updated",
    body: "Update for ticket {{ticket_id}}:\nCurrent status: {{ticket_status}}\nUpdated by: {{updated_by}}",
    html: buildThemedEmailHtml(inner.ticketCreated),
  },
  passwordReset: {
    subject: "Reset your {{workspace_name}} password",
    body: "We received a request to reset your password.\nUse this link to continue:\n{{reset_link}}\n\nIf you did not request this, ignore this email.",
    html: buildThemedEmailHtml(inner.passwordReset),
  },
};
